import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Journal from './Journal';
import AdminPanel from './AdminPanel';
import Analytics from './Analytics';
import MonteCarlo from './MonteCarlo';
import DailyBias from './DailyBias';
import GoldTicker from './GoldTicker';
import AICoach from './AICoach';
import RiskManager from './RiskManager';
import Achievements from './Achievements';
import Billing from './Billing';

// ── LOGO ──
const Logo = () => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" style={{width:'100%',height:'100%',display:'block'}}>
    <defs>
      <linearGradient id="lg-app" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F0CC6E"/>
        <stop offset="50%" stopColor="#C9973A"/>
        <stop offset="100%" stopColor="#8B6020"/>
      </linearGradient>
    </defs>
    <circle cx="60" cy="60" r="58" fill="#0a0a12" stroke="url(#lg-app)" strokeWidth="1.5"/>
    <ellipse cx="60" cy="72" rx="28" ry="22" fill="none" stroke="url(#lg-app)" strokeWidth="1.8"/>
    <circle cx="60" cy="38" r="14" fill="none" stroke="url(#lg-app)" strokeWidth="1.8"/>
    <circle cx="60" cy="34" r="4" fill="#C9973A"/>
    <path d="M46 60 Q38 55 36 68 Q34 80 60 85 Q86 80 84 68 Q82 55 74 60" fill="none" stroke="url(#lg-app)" strokeWidth="1.8"/>
    <line x1="48" y1="72" x2="72" y2="72" stroke="url(#lg-app)" strokeWidth="1.2"/>
    <line x1="50" y1="77" x2="70" y2="77" stroke="url(#lg-app)" strokeWidth="1"/>
    <path d="M30 55 L20 45 L38 50" fill="none" stroke="#E2B84A" strokeWidth="1.5"/>
    <path d="M90 55 L100 45 L82 50" fill="none" stroke="#E2B84A" strokeWidth="1.5"/>
    <circle cx="60" cy="90" r="8" fill="none" stroke="url(#lg-app)" strokeWidth="1.5"/>
    <text x="60" y="94" textAnchor="middle" fill="#C9973A" fontSize="9" fontWeight="bold">₿</text>
    <text x="22" y="42" textAnchor="middle" fill="#C9973A" fontSize="8" fontWeight="bold">₿</text>
    <text x="98" y="42" textAnchor="middle" fill="#C9973A" fontSize="7" fontWeight="bold">Ξ</text>
  </svg>
);

// ── SESSION LOGIC ──
// XAUUSD market hours: Closes Friday 22:00 UTC, reopens Sunday 22:00 UTC
function isMarketClosed() {
  const now = new Date();
  const day = now.getUTCDay(); // 0=Sun, 1=Mon, ... 5=Fri, 6=Sat
  const h = now.getUTCHours();
  const m = now.getUTCMinutes();
  const t = h * 60 + m;

  // Saturday — closed all day
  if (day === 6) return true;
  // Friday after 22:00 UTC — closed
  if (day === 5 && t >= 22 * 60) return true;
  // Sunday before 22:00 UTC — closed
  if (day === 0 && t < 22 * 60) return true;

  return false;
}

function getSessionStates() {
  const now = new Date();
  const h = now.getUTCHours(), m = now.getUTCMinutes();
  const t = h * 60 + m;
  const closed = isMarketClosed();

  return [
    { name:'Asia', time:'00:00–09:00 UTC', start:0, end:540, primeRange:[0, 90] },
    { name:'London', time:'08:00–16:30 UTC', start:480, end:990, primeRange:[480, 570] },
    { name:'NY Overlap', time:'13:00–16:30 UTC', start:780, end:990, primeRange:[780, 990] },
    { name:'New York', time:'13:00–21:00 UTC', start:780, end:1260, primeRange:[780, 870] },
  ].map(s => ({
    ...s,
    open: !closed && t >= s.start && t < s.end,
    prime: !closed && t >= s.primeRange[0] && t < s.primeRange[1],
    marketClosed: closed
  }));
}

// ── CLOCK HOOK ──
function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ══════════════════════════════════════════════════
// PAGE: DASHBOARD
// ══════════════════════════════════════════════════
function Dashboard({ onNav }) {
  const time = useClock();
  const sessions = getSessionStates();
  const activeSession = sessions.find(s => s.open && s.prime) || sessions.find(s => s.open);
  const utc = time.toUTCString().slice(17, 25);

  return (
    <div>
      <div className="dashboard-hero">
        <div style={{width:60,height:60,overflow:'hidden',flexShrink:0}}><Logo /></div>
        <div className="dashboard-hero-text">
          <h1>Trading Buddha · <em>XAUUSD Pro</em></h1>
          <p>Institutional-grade gold trading framework. Precision entries. Risk-first discipline.</p>
        </div>
      </div>

      <div className="kpi-grid">
        {[
          {label:'TIMEFRAMES', val:'4H · 1H · 15M', note:'Top-down analysis'},
          {label:'WIN RATE', val:'47–53%', note:'Expected range'},
          {label:'TARGET RR', val:'1:2.5+', note:'Minimum 1:2'},
          {label:'TRADES/WEEK', val:'3–6', note:'Quality over quantity'},
          {label:'MAX RISK', val:'1% / trade', note:'Non-negotiable'},
          {label:'MAX DAILY DD', val:'2%', note:'Stop trading at 2%'},
        ].map(k => (
          <div key={k.label} className="kpi-cell">
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.val}</div>
            <div className="kpi-note">{k.note}</div>
          </div>
        ))}
      </div>

      <h3 style={{fontFamily:'Bebas Neue,sans-serif',fontSize:14,letterSpacing:2,color:'var(--text-3)',marginBottom:12}}>
        LIVE SESSION STATUS · {utc} UTC
      </h3>
      {sessions[0]?.marketClosed && (
        <div className="weekend-banner mb-16">
          <div className="weekend-banner-icon">🌙</div>
          <div>
            <div className="weekend-banner-title">XAUUSD Market Closed — Weekend</div>
            <div className="weekend-banner-sub">Market reopens Sunday 22:00 UTC. Use this time to review trades, study the strategy, and plan next week.</div>
          </div>
        </div>
      )}
      <div className="session-cards">
        {sessions.map(s => (
          <div key={s.name} className={`session-card ${s.open && s.prime ? 'prime' : s.open ? 'open' : ''}`}>
            <div className="session-card-name">{s.name}</div>
            <div className="session-card-time">{s.time}</div>
            <div className="session-card-status">
              <span className={`badge ${s.open && s.prime ? 'gold' : s.open ? 'green' : 'grey'}`}>
                {s.open && s.prime ? '● PRIME' : s.open ? '● OPEN' : '○ Closed'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="card mt-24">
        <div className="card-title">THE EDGE EXPLAINED</div>
        <p style={{fontSize:14, color:'var(--text-2)', lineHeight:1.8}}>
          XAUUSD moves an average of $100–200 per day. This framework targets <strong style={{color:'var(--gold-2)'}}>institutional liquidity sweeps</strong> —
          moments when smart money hunts stop losses before reversing. By waiting for the sweep,
          the 4H structural confirmation, and the 15M entry trigger, you enter where the odds are
          genuinely in your favor — not in random noise.
        </p>
      </div>

      <div className="section-row mt-24">
        {[
          {icon:'🧮', title:'Position Calculator', sub:'Calc exact lot size before every trade', page:'calculator'},
          {icon:'✅', title:'Pre-Trade Checklist', sub:'Validate all 12 conditions', page:'checklist'},
          {icon:'📓', title:'Trade Journal', sub:'Log and review your trades', page:'journal'},
          {icon:'📖', title:'Strategy Logic', sub:'The full 5-condition framework', page:'strategy'},
        ].map(c => (
          <div key={c.title} className="card" style={{cursor:'pointer'}} onClick={() => onNav(c.page)}>
            <div style={{fontSize:24,marginBottom:10}}>{c.icon}</div>
            <div style={{fontFamily:'Bebas Neue,sans-serif',fontSize:15,letterSpacing:1.5,color:'var(--gold-2)',marginBottom:4}}>{c.title}</div>
            <div style={{fontSize:12,color:'var(--text-3)'}}>{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: POSITION CALCULATOR
// ══════════════════════════════════════════════════
function Calculator() {
  const [balance, setBalance] = useState(10000);
  const [riskPct, setRiskPct] = useState(1);
  const [stopPips, setStopPips] = useState(200);
  const [rrRatio, setRrRatio] = useState(2.5);

  const dollarRisk = balance * (riskPct / 100);
  const pipValue = 10; // XAUUSD: $10 per pip (1 lot, standard)
  const lotSize = stopPips > 0 ? (dollarRisk / (stopPips * pipValue)).toFixed(2) : 0;
  const tpPips = stopPips * rrRatio;
  const tpProfit = (lotSize * tpPips * pipValue).toFixed(2);
  const beWinPct = rrRatio > 0 ? ((1 / (1 + rrRatio)) * 100).toFixed(1) : '—';

  let riskLevel = 'Optimal', riskClass = 'green';
  if (riskPct > 2) { riskLevel = 'Too High'; riskClass = 'red'; }
  else if (riskPct > 1) { riskLevel = 'Elevated'; riskClass = 'amber'; }

  const rorPct = Math.min(99, (riskPct * 8)).toFixed(1);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Position <em>Calculator</em></h1>
        <p className="page-subtitle">Calculate exact lot sizes before every trade</p>
      </div>
      <div className="calc-grid">
        <div className="card">
          <div className="card-title">INPUTS</div>
          <div className="calc-inputs">
            {[
              {label:'Account Balance ($)', val:balance, set:setBalance, min:500, max:500000, step:500, fmt:v=>'$'+Number(v).toLocaleString()},
              {label:'Risk Percentage (%)', val:riskPct, set:setRiskPct, min:0.1, max:5, step:0.1, fmt:v=>v+'%'},
              {label:'Stop Distance (pips)', val:stopPips, set:setStopPips, min:10, max:1000, step:5, fmt:v=>v+' pips'},
              {label:'Risk:Reward Ratio', val:rrRatio, set:setRrRatio, min:1, max:10, step:0.5, fmt:v=>'1:'+v},
            ].map(f => (
              <div key={f.label} className="calc-field">
                <label>{f.label}</label>
                <div className="range-row">
                  <input type="range" min={f.min} max={f.max} step={f.step} value={f.val}
                    onChange={e => f.set(parseFloat(e.target.value))} style={{flex:1}} />
                  <span className="range-val">{f.fmt(f.val)}</span>
                </div>
                <input type="number" value={f.val} step={f.step} min={f.min} max={f.max}
                  onChange={e => f.set(parseFloat(e.target.value) || f.min)}
                  style={{width:'100%', marginTop:4}} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="card">
            <div className="card-title">OUTPUTS</div>
            <div className="calc-outputs">
              <div className="output-row">
                <span className="output-label">Dollar Risk</span>
                <span className="output-value">${dollarRisk.toFixed(2)}</span>
              </div>
              <div className="output-row">
                <span className="output-label">Lot Size</span>
                <span className="output-value gold">{lotSize} lots</span>
              </div>
              <div className="output-row">
                <span className="output-label">Pip Value (1 lot)</span>
                <span className="output-value">$10.00</span>
              </div>
              <div className="output-row">
                <span className="output-label">TP Distance</span>
                <span className="output-value">{tpPips.toFixed(0)} pips</span>
              </div>
              <div className="output-row">
                <span className="output-label">Target Profit</span>
                <span className="output-value green">+${tpProfit}</span>
              </div>
              <div className="output-row">
                <span className="output-label">Breakeven Win%</span>
                <span className="output-value">{beWinPct}%</span>
              </div>
              <div className="output-row">
                <span className="output-label">Risk Assessment</span>
                <span className={`output-value ${riskClass}`}>{riskLevel}</span>
              </div>
            </div>
          </div>
          <div className="card mt-16">
            <div className="card-title">RISK OF RUIN ESTIMATE</div>
            <div className="risk-bar-wrap">
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--text-3)',marginBottom:4}}>
                <span>Estimated Ruin Risk</span>
                <span style={{fontFamily:'DM Mono,monospace',color:`var(--${riskClass})`}}>{rorPct}%</span>
              </div>
              <div className="risk-bar-track">
                <div className={`risk-bar-fill ${riskClass}`} style={{width:`${rorPct}%`}}></div>
              </div>
              <p style={{fontSize:12,color:'var(--text-3)'}}>
                {riskLevel === 'Optimal' ? 'At 1% risk you can withstand 50+ consecutive losses before a 50% drawdown.' :
                 riskLevel === 'Elevated' ? 'Above 1% risk. Risk of ruin increases non-linearly. Reduce if possible.' :
                 'High risk per trade. At 3%+ you can reach a 50% drawdown in under 25 losses. Reduce immediately.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="card mt-24">
        <div className="card-title">FORMULA REFERENCE</div>
        <div className="formula-block">{`Dollar Risk   = Account Balance × (Risk% / 100)
            = $${balance.toLocaleString()} × ${riskPct}% = $${dollarRisk.toFixed(2)}

Lot Size      = Dollar Risk ÷ (Stop Pips × Pip Value)
            = $${dollarRisk.toFixed(2)} ÷ (${stopPips} × $10) = ${lotSize} lots

TP Pips       = Stop Pips × RR Ratio
            = ${stopPips} × ${rrRatio} = ${tpPips.toFixed(0)} pips

Target Profit = Lot Size × TP Pips × Pip Value
            = ${lotSize} × ${tpPips.toFixed(0)} × $10 = $${tpProfit}

Breakeven %   = 1 ÷ (1 + RR) = ${beWinPct}% win rate to break even`}</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: STRATEGY LOGIC
// ══════════════════════════════════════════════════
function Strategy() {
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title"><em>Strategy Logic</em></h1>
        <p className="page-subtitle">The 5-condition institutional entry framework for XAUUSD</p>
      </div>
      <div className="card mb-24">
        <div className="card-title">5-CONDITION ENTRY FRAMEWORK</div>
        <ol className="numbered-list">
          {[
            {title:'4H Structural Bias', desc:'Identify the dominant trend on the 4H chart. Mark premium and discount zones. Only trade in the direction of the 4H bias — no counter-trend trades unless all 5 conditions are still met on the other side.'},
            {title:'Liquidity Level Identified', desc:'Mark the nearest significant liquidity pool: equal highs/lows, previous day high/low, previous London/NY session extremes. Price is drawn to liquidity. Know exactly where the magnets are before any session.'},
            {title:'Liquidity Sweep Confirmed on Close', desc:'Wait for a confirmed candle close beyond the liquidity level — not a wick. The sweep must be on the 15M or higher timeframe. This is the hunt: smart money clearing stops before the real move.'},
            {title:'1H Change of Character (CHoCH)', desc:'After the sweep, the 1H chart must show a structural shift — a Change of Character. A higher high (for longs) or lower low (for shorts) on 1H confirms the internal structure has shifted. Do not skip this step.'},
            {title:'15M Entry Trigger', desc:'Drop to 15M. Wait for a bullish/bearish engulfing candle, a fair value gap fill entry, or a 15M CHoCH that aligns with the higher-timeframe bias. This is your precision entry — not the 4H candle close.'},
          ].map((item, i) => (
            <li key={i} className="numbered-item">
              <span className="numbered-item-num">{i+1}</span>
              <div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="section-row">
        <div className="card">
          <div className="card-title">STOP LOSS PLACEMENT</div>
          <p style={{fontSize:13,color:'var(--text-3)',lineHeight:1.8}}>
            Always place stops <strong style={{color:'var(--text-1)'}}>beyond structure</strong> — not at a round number.
            For longs: below the sweep low by 3–5 pips. For shorts: above the sweep high by 3–5 pips.
            If the stop requires more than 250 pips, skip the trade. Never widen stops after entry.
          </p>
        </div>
        <div className="card">
          <div className="card-title">TP STRUCTURE</div>
          <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:4}}>
            {[
              {label:'TP1 — 50%', desc:'First liquidity level / 1:1.5R', color:'var(--gold-3)'},
              {label:'TP2 — 40%', desc:'Next major structure / 1:3R', color:'var(--gold-2)'},
              {label:'Runner — 10%', desc:'Full objective / trail stop', color:'var(--gold-1)'},
            ].map(t => (
              <div key={t.label} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'var(--bg-3)',borderRadius:6}}>
                <span style={{fontFamily:'Bebas Neue,sans-serif',fontSize:14,color:t.color,minWidth:90}}>{t.label}</span>
                <span style={{fontSize:12,color:'var(--text-3)'}}>{t.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="card mt-20">
        <div className="card-title">WHEN NOT TO TRADE</div>
        <div className="pill-list">
          {['30 min before/after FOMC','30 min before/after CPI / NFP','Daily loss limit reached','Spread > 5 pips','Less than 1:2 RR achievable','No clear liquidity level visible','4H bias unclear','Setup does not match all 5 conditions'].map(p => (
            <span key={p} className="pill red">{p}</span>
          ))}
        </div>
      </div>
      <div className="card mt-20">
        <div className="card-title">AVOIDING FAKE BREAKOUTS</div>
        <p style={{fontSize:13,color:'var(--text-3)',lineHeight:1.8,marginBottom:12}}>
          The most common trap is entering on the breakout itself — before confirmation. The framework
          deliberately waits for the sweep (the fake break) and the CHoCH before entering. This means
          you will miss some moves. That is acceptable — it eliminates the majority of false signals.
        </p>
        <p style={{fontSize:13,color:'var(--text-3)',lineHeight:1.8}}>
          Key rule: <strong style={{color:'var(--gold-2)'}}>wait for the candle to close.</strong> A wick through a level is not a sweep.
          A close through the level followed by a reversal candle — that is the signal.
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: INDICATORS
// ══════════════════════════════════════════════════
function Indicators() {
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title"><em>Indicators</em></h1>
        <p className="page-subtitle">Only use tools that support price action — not predict it</p>
      </div>
      <div className="card mb-24">
        <div className="card-title">APPROVED TOOLS</div>
        <div className="ind-grid">
          <div className="ind-grid-header">
            <div>TOOL</div>
            <div>TIMEFRAME</div>
            <div>PURPOSE</div>
            <div>LIMITATION</div>
          </div>
          {[
            {tool:'EMA 20', tf:'4H / 1H', purpose:'Dynamic support/resistance reference. Not an entry signal — just context for trend direction.', limit:'Lags. Use as confluence only, never the primary entry reason.'},
            {tool:'EMA 50', tf:'4H', purpose:'Medium-term trend bias. If price is above EMA 50 on 4H, bias is bullish by default.', limit:'Lags heavily in ranging markets. Ignore in tight ranges.'},
            {tool:'ATR (14)', tf:'4H / Daily', purpose:'Measure of daily volatility. Use to confirm that a 1:2.5 TP is realistic within today\'s range.', limit:'Historical. Does not predict future volatility.'},
            {tool:'Volume', tf:'15M', purpose:'Confirm sweep candles. High volume on the sweep candle = stronger conviction that smart money participated.', limit:'XAUUSD volume data is broker-dependent. Not perfect.'},
            {tool:'VWAP', tf:'15M / 1H (intraday)', purpose:'Institutional reference price. Entries near VWAP with structural confluence are higher probability.', limit:'Resets daily. Loses significance near EOD / session boundaries.'},
          ].map(r => (
            <div className="ind-grid-row" key={r.tool}>
              <div className="ind-grid-cell ind-cell-tool">{r.tool}</div>
              <div className="ind-grid-cell ind-cell-tf">{r.tf}</div>
              <div className="ind-grid-cell ind-cell-purpose">{r.purpose}</div>
              <div className="ind-grid-cell ind-cell-limit">{r.limit}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="card-title">WHAT TO AVOID</div>
        <div className="section-row">
          {[
            {name:'RSI', reason:'Overbought/oversold is meaningless in trending gold markets. Price can stay "overbought" for weeks.'},
            {name:'Bollinger Bands', reason:'The squeeze setup works in crypto; in gold it generates far too many false signals.'},
            {name:'MACD', reason:'MACD crossovers lag by 10–20 candles. By the time it signals, the move is 60% done.'},
            {name:'Fibonacci Retracements', reason:'Subjectivity in swing selection makes Fibonacci levels unreliable without a price action confirmation framework.'},
          ].map(t => (
            <div key={t.name} style={{background:'rgba(224,82,82,0.06)',border:'1px solid rgba(224,82,82,0.2)',borderRadius:6,padding:'16px'}}>
              <div style={{fontFamily:'Bebas Neue,sans-serif',fontSize:16,color:'var(--red)',marginBottom:6}}>{t.name}</div>
              <p style={{fontSize:12,color:'var(--text-3)',lineHeight:1.6}}>{t.reason}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: SESSIONS & MACRO
// ══════════════════════════════════════════════════
function Sessions() {
  const time = useClock();
  const sessions = getSessionStates();
  const utc = time.toUTCString().slice(17, 25);

  const correlations = [
    {label:'DXY (US Dollar)', val:-0.75, note:'Gold typically moves inversely to the dollar. Strong DXY = headwind for gold longs.'},
    {label:'US 10Y Yield', val:-0.65, note:'Rising yields increase the opportunity cost of holding gold. Falling yields support gold.'},
    {label:'S&P 500', val:0.15, note:'Correlation is weak and variable. Risk-off flows can send both lower simultaneously.'},
    {label:'Crude Oil', val:0.45, note:'Both commodities. Moderate positive correlation via inflation and USD dynamics.'},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Sessions & <em>Macro</em></h1>
        <p className="page-subtitle">Live session status + macro correlation reference</p>
      </div>
      <h3 style={{fontFamily:'Bebas Neue,sans-serif',fontSize:14,letterSpacing:2,color:'var(--text-3)',marginBottom:12}}>
        LIVE SESSION STATUS · {utc} UTC
      </h3>
      {sessions[0]?.marketClosed && (
        <div className="weekend-banner mb-16">
          <div className="weekend-banner-icon">🌙</div>
          <div>
            <div className="weekend-banner-title">XAUUSD Market Closed — Weekend</div>
            <div className="weekend-banner-sub">Market reopens Sunday 22:00 UTC. Use this time to review trades, study the strategy, and plan next week.</div>
          </div>
        </div>
      )}
      <div className="session-cards">
        {sessions.map(s => (
          <div key={s.name} className={`session-card ${s.open && s.prime ? 'prime' : s.open ? 'open' : ''}`}>
            <div className="session-card-name">{s.name}</div>
            <div className="session-card-time">{s.time}</div>
            <div className="session-card-status">
              <span className={`badge ${s.open && s.prime ? 'gold' : s.open ? 'green' : 'grey'}`}>
                {s.open && s.prime ? '● PRIME' : s.open ? '● OPEN' : '○ Closed'}
              </span>
            </div>
            <p style={{fontSize:11,color:'var(--text-4)',marginTop:8,lineHeight:1.5}}>
              {s.name === 'Asia' ? 'Ranging, liquidity building. Mark levels for London.' :
               s.name === 'London' ? 'Primary session. Most setups trigger here.' :
               s.name === 'NY Overlap' ? 'Highest liquidity. Best trade execution window.' :
               'Continuation of London moves. Can reverse at EOD.'}
            </p>
          </div>
        ))}
      </div>

      <div className="card mt-24">
        <div className="card-title">MACRO CORRELATIONS</div>
        {correlations.map(c => (
          <div key={c.label} className="corr-item">
            <span className="corr-label">{c.label}</span>
            <span className="corr-val" style={{color: c.val < 0 ? 'var(--red)' : 'var(--green)'}}>{c.val > 0 ? '+' : ''}{c.val}</span>
            <div className="corr-bar-wrap">
              <div className={`corr-bar ${c.val < 0 ? 'corr-neg' : 'corr-pos'}`}
                style={{width:`${Math.abs(c.val) * 100}%`}}></div>
            </div>
            <span style={{fontSize:11,color:'var(--text-4)',maxWidth:260}}>{c.note}</span>
          </div>
        ))}
      </div>

      <div className="card mt-24">
        <div className="card-title">NEWS HANDLING</div>
        <div className="news-grid">
          {[
            {title:'FOMC', desc:'Avoid 30 min before + 30 min after. Extreme volatility. Wait for a new session structure to form.'},
            {title:'CPI / NFP', desc:'Same rule as FOMC. These are the highest-impact events for gold. Never hold through.'},
            {title:'Geopolitical Events', desc:'Gold-positive surprises spike up 1–3%. If you\'re already in a long, let TP1 hit and trail. Don\'t chase entries.'},
            {title:'DXY Breakouts', desc:'A DXY breakout above resistance often sends gold to key support. Check DXY before every gold entry.'},
          ].map(n => (
            <div key={n.title} className="news-card">
              <div className="news-card-title">{n.title}</div>
              <p className="news-card-desc">{n.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: PRE-TRADE CHECKLIST
// ══════════════════════════════════════════════════
const CHECKLIST_ITEMS = [
  '4H bias is clear and confirmed',
  'Liquidity level identified on chart',
  'Sweep confirmed on candle close',
  '1H CHoCH is confirmed',
  '15M entry trigger is valid',
  'Stop structurally placed beyond sweep',
  'Minimum 1:2 RR is achievable',
  'Lot size calculated in advance',
  'DXY checked for confirmation',
  'News calendar checked — no red events',
  'Session timing is valid (London or NY)',
  'Daily loss cap not reached',
];

function Checklist() {
  const [checked, setChecked] = useState([]);

  function toggle(i) {
    setChecked(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  }
  const pct = Math.round((checked.length / CHECKLIST_ITEMS.length) * 100);
  const complete = checked.length === CHECKLIST_ITEMS.length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Pre-Trade <em>Checklist</em></h1>
        <p className="page-subtitle">All 12 conditions must be confirmed before entering a trade</p>
      </div>
      <div className="card mb-24">
        <div className="progress-bar-wrap">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <span className="progress-label">{checked.length} / {CHECKLIST_ITEMS.length} conditions confirmed</span>
            <span className="progress-label" style={{color:'var(--gold-2)'}}>{pct}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{width:`${pct}%`}}></div>
          </div>
          {complete ? (
            <div className="success-banner mt-12" style={{textAlign:'center',fontFamily:'Bebas Neue,sans-serif',fontSize:16,letterSpacing:2}}>
              ✓ ALL CONDITIONS CONFIRMED — VALID TO ENTER
            </div>
          ) : (
            <div className={`progress-label ${complete ? 'complete' : ''}`} style={{marginTop:8}}>
              {CHECKLIST_ITEMS.length - checked.length} conditions remaining before entry is valid
            </div>
          )}
        </div>
        <div className="checklist" style={{marginTop:16}}>
          {CHECKLIST_ITEMS.map((item, i) => (
            <div key={i} className={`check-item ${checked.includes(i) ? 'checked' : ''}`} onClick={() => toggle(i)}>
              <div className="check-box">{checked.includes(i) ? '✓' : ''}</div>
              <span className="check-label">{item}</span>
            </div>
          ))}
        </div>
        <div style={{marginTop:16}}>
          <button className="btn-sm ghost" onClick={() => setChecked([])}>Reset All</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: TRADE EXAMPLES
// ══════════════════════════════════════════════════
function Accordion({ title, meta, badge, badgeClass, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="accordion">
      <div className="accordion-header" onClick={() => setOpen(!open)}>
        <span className="accordion-title">{title}</span>
        <div className="accordion-meta">
          {meta.map(m => <span key={m} style={{fontSize:12,color:'var(--text-3)',fontFamily:'DM Mono,monospace'}}>{m}</span>)}
          <span className={`badge ${badgeClass}`}>{badge}</span>
          <span className={`accordion-chevron ${open ? 'open' : ''}`}>▼</span>
        </div>
      </div>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

function TradeExamples() {
  const examples = [
    {
      title:'TRADE 1 — XAUUSD LONG',
      meta:['London Open','Feb 2024'],
      badge:'+2.8R',
      badgeClass:'green',
      metrics:[{k:'Direction',v:'Long'},{k:'Session',v:'London'},{k:'Entry',v:'$2,032.50'},{k:'R-Multiple',v:'+2.8R'}],
      details:[
        ['Setup','Asia range sweep + London CHoCH'],
        ['4H Bias','Bullish — above EMA 50, premium zone'],
        ['Liquidity Level','Equal lows at $2,028.00 (Asia lows)'],
        ['Sweep','Wick to $2,027.40, closed at $2,029.00'],
        ['1H CHoCH','Confirmed. 1H HH printed at 08:45 UTC'],
        ['15M Trigger','Bullish engulfing at $2,030.50'],
        ['Stop Loss','$2,026.80 (below sweep low)'],
        ['TP1 (50%)','$2,042.00 — hit at 09:30 UTC'],
        ['TP2 (40%)','$2,049.50 — hit at 11:15 UTC'],
        ['Runner (10%)','Trailed, closed $2,051.20 at NY open'],
        ['Checklist','All 12 confirmed'],
        ['Lesson','Asia range sweeps followed by London CHoCH = highest probability setup in this system.'],
      ]
    },
    {
      title:'TRADE 2 — XAUUSD SHORT',
      meta:['NY Overlap','Mar 2024'],
      badge:'+3.1R',
      badgeClass:'green',
      metrics:[{k:'Direction',v:'Short'},{k:'Session',v:'NY Overlap'},{k:'Entry',v:'$2,164.20'},{k:'R-Multiple',v:'+3.1R'}],
      details:[
        ['Setup','Liquidity sweep of London high + 1H CHoCH'],
        ['4H Bias','Bearish — rejected from 4H premium zone at $2,170'],
        ['Liquidity Level','London high at $2,161.80 (equal highs)'],
        ['Sweep','Close above $2,162.00 then immediate rejection'],
        ['1H CHoCH','Lower low confirmed at 14:15 UTC'],
        ['15M Trigger','Bearish engulfing at $2,163.80'],
        ['Stop Loss','$2,166.50 (above sweep high)'],
        ['TP1 (50%)','$2,154.00 — hit at 15:00 UTC'],
        ['TP2 (40%)','$2,146.50 — hit at 16:00 UTC'],
        ['Runner (10%)','Closed at $2,143.20 at NY close'],
        ['Checklist','All 12 confirmed'],
        ['Lesson','NY Overlap sweeps of London session highs/lows are the most reliable short setups in the system.'],
      ]
    },
    {
      title:'TRADE 3 — SCRATCH / BREAKEVEN',
      meta:['London','Apr 2024'],
      badge:'Scratch',
      badgeClass:'grey',
      metrics:[{k:'Direction',v:'Long'},{k:'Session',v:'London'},{k:'Entry',v:'$2,298.40'},{k:'R-Multiple',v:'0R'}],
      details:[
        ['Setup','Sweep of Asia lows — but 1H CHoCH was weak'],
        ['4H Bias','Bullish, but approaching 4H supply zone'],
        ['Liquidity Level','Asia lows at $2,294.00'],
        ['Sweep','Clean sweep to $2,293.20'],
        ['1H CHoCH','Technically confirmed but only 1H candle'],
        ['15M Trigger','Entry at $2,298.40'],
        ['Stop Loss','$2,292.00'],
        ['Action','Moved stop to breakeven at 1:1. Hit, exited at $2,298.40'],
        ['R-Multiple','0R — flat trade'],
        ['Checklist','11/12 — supply zone concern noted'],
        ['Lesson','When the 1H CHoCH is marginal and you\'re entering near a 4H supply zone, move to breakeven at the first 1:1. The scratch is a win — capital preserved.'],
      ]
    }
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Trade <em>Examples</em></h1>
        <p className="page-subtitle">Real annotated setups showing the 5-condition framework in action</p>
      </div>
      {examples.map(ex => (
        <Accordion key={ex.title} title={ex.title} meta={ex.meta} badge={ex.badge} badgeClass={ex.badgeClass}>
          <div className="acc-metrics">
            {ex.metrics.map(m => (
              <div key={m.k}>
                <div className="acc-metric-val">{m.v}</div>
                <div className="acc-metric-label">{m.k}</div>
              </div>
            ))}
          </div>
          <div className="acc-detail-grid">
            {ex.details.map(([k, v]) => (
              <div key={k} className="acc-detail-row">
                <span className="acc-detail-key">{k}</span>
                <span className="acc-detail-val">{v}</span>
              </div>
            ))}
          </div>
        </Accordion>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: FATAL MISTAKES
// ══════════════════════════════════════════════════
function FatalMistakes() {
  const mistakes = [
    {title:'Entering Before the Sweep', desc:'You see the liquidity level and think the reversal is coming. You enter early. The sweep runs your stop, then reverses exactly as you predicted — without you.', lesson:'The sweep IS the entry. Enter after, not before.'},
    {title:'Ignoring the 4H Bias', desc:'You find a beautiful 15M setup going short. But the 4H is bullish. You take it anyway. Price dips slightly then runs 300 pips against you.', lesson:'The 4H bias is the law. 15M setups against 4H never get taken.'},
    {title:'Moving Stops Further', desc:'Price is slightly against you. You move the stop to give it more room. Now it\'s a 3% risk trade. It keeps going.', lesson:'Stop placement is decided before entry. It does not change after.'},
    {title:'Chasing After Missing the Entry', desc:'You watched the setup, missed the entry, and now price is moving. You jump in at market. You\'re now at 1:0.8 RR at best.', lesson:'Missed trades do not exist. The next setup exists.'},
    {title:'Trading Through News Events', desc:'CPI is in 10 minutes. You have a great setup. You take it. Gold moves $30 against you in 3 minutes.', lesson:'Calendar events are non-negotiable. No trades 30 min before/after.'},
    {title:'Oversizing After Losses', desc:'You\'re down 3% this week. You take a 3% risk trade to recover. It loses. Now you\'re down 6%.', lesson:'Risk stays at 1% regardless of recent performance.'},
    {title:'Taking Marginal Setups', desc:'The checklist has 9/12. Close enough, you think. But the 3 missing items are the sweep confirmation, the CHoCH, and the news check.', lesson:'12/12 or no trade. The checklist is not a suggestion.'},
    {title:'Not Confirming on the 1H', desc:'You see the 4H bias, the liquidity level, the sweep. You go straight to 15M. The 1H CHoCH never formed. You\'re trading against structure.', lesson:'4H → 1H → 15M. Never skip the 1H confirmation step.'},
    {title:'Trading Without a Calculated Lot Size', desc:'You enter with a "rough estimate" of lot size. Your actual risk is 2.3% because you didn\'t calculate. One loss removes a month of gains.', lesson:'Open the calculator. Enter the numbers. This takes 45 seconds.'},
    {title:'Holding Losers Overnight', desc:'XAUUSD gaps on open. Your Asian-session stop is now at a 5% loss. You didn\'t want to take the original stop.', lesson:'Respect every stop. Gaps happen. The original stop was correct.'},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Fatal <em>Mistakes</em></h1>
        <p className="page-subtitle">The 10 errors that destroy accounts. Know them. Avoid them. Always.</p>
      </div>
      <ul className="fatal-list">
        {mistakes.map((m, i) => (
          <li key={i} className="fatal-item">
            <span className="fatal-num">{String(i+1).padStart(2,'0')}</span>
            <div>
              <div className="fatal-title">{m.title}</div>
              <div className="fatal-desc">{m.desc}</div>
              <div className="fatal-lesson">Lesson: {m.lesson}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PAGE: 30-DAY PLAN
// ══════════════════════════════════════════════════
function ThirtyDayPlan() {
  const weeks = [
    {num:'Week 01', title:'Structure Immersion', desc:'Spend 2 hours per day on TradingView. Mark every significant liquidity level on the XAUUSD daily chart going back 3 months. Identify 10 historical sweep entries. Do not place any trades. The goal is to train your eyes to see liquidity before price reaches it.'},
    {num:'Week 02', title:'Forward Setup Identification', desc:'Every day before London opens, identify one potential setup: the liquidity level, the expected sweep direction, and the entry plan. Write it in your journal. Track how often your identified setups actually trigger. Do not trade yet.'},
    {num:'Week 03', title:'Demo Execution', desc:'Execute all setups on a demo account. Use exact position sizing from the calculator. Complete the checklist for every trade. Build the habit before capital is at risk. Track win rate and RR on demo.'},
    {num:'Week 04', title:'Live Micro-Sizing', desc:'Move to live with 0.01 lot maximum. The goal is not profit — it is executing the framework exactly as practiced. Any deviation from the 5 conditions or the checklist is a failed trade, even if it made money.'},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">30-Day <em>Onboarding Plan</em></h1>
        <p className="page-subtitle">A structured four-week path from zero to live execution</p>
      </div>
      <div className="card mb-24">
        <div className="plan-timeline">
          {weeks.map(w => (
            <div key={w.num} className="plan-week">
              <div className="plan-dot"></div>
              <div className="plan-week-num">{w.num}</div>
              <div className="plan-week-title">{w.title}</div>
              <div className="plan-week-desc">{w.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="card mb-24">
        <div className="card-title">EXPECTANCY MATH</div>
        <div className="formula-block">{`Expected Value = (Win Rate × Avg Win) − (Loss Rate × Avg Loss)

At 50% win rate, 1:2.5 average RR:
  = (0.50 × 2.5R) − (0.50 × 1R)
  = 1.25R − 0.50R
  = +0.75R per trade

At 47% win rate (conservative):
  = (0.47 × 2.5R) − (0.53 × 1R)
  = 1.175R − 0.53R
  = +0.645R per trade

Positive expectancy = edge. Positive edge + discipline = compounding.`}</div>
      </div>
      <div className="card">
        <div className="card-title">DAILY ROUTINE</div>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {[
            ['06:00 UTC', 'Check DXY, US 10Y. Read overnight news.'],
            ['07:30 UTC', 'Mark London session liquidity levels. Run pre-trade checklist.'],
            ['08:00 UTC', 'London open. Monitor identified setups only.'],
            ['09:30 UTC', 'First hour complete. Either in a trade or not — no chasing.'],
            ['13:00 UTC', 'NY Overlap. Reassess. Check news calendar for afternoon events.'],
            ['16:30 UTC', 'London close. If no trade taken today, journal the missed setups and why.'],
            ['EOD', 'Log trade (if taken) in journal. Review checklist compliance. Study 30 min.'],
          ].map(([time, task]) => (
            <div key={time} style={{display:'flex',gap:16,padding:'10px 14px',background:'var(--bg-3)',borderRadius:6}}>
              <span style={{fontFamily:'DM Mono,monospace',fontSize:12,color:'var(--gold-2)',minWidth:80,flexShrink:0}}>{time}</span>
              <span style={{fontSize:13,color:'var(--text-2)'}}>{task}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// SIDEBAR NAV STRUCTURE
// ══════════════════════════════════════════════════
const NAV = [
  { section: null, items: [
    { id: 'dashboard', label: 'Dashboard', icon: '◈' },
  ]},
  { section: 'AI COACH', items: [
    { id: 'aicoach', label: 'AI Coach', icon: '✦', highlight: true },
  ]},
  { section: 'TOOLS', items: [
    { id: 'calculator', label: 'Position Calc', icon: '⊞' },
    { id: 'journal', label: 'Trade Journal', icon: '◧' },
    { id: 'risk', label: 'Risk Manager', icon: '⛨' },
    { id: 'analytics', label: 'Analytics', icon: '◔' },
    { id: 'montecarlo', label: 'Monte Carlo', icon: '◐' },
    { id: 'bias', label: 'Daily Bias', icon: '◑' },
    { id: 'checklist', label: 'Pre-Trade Check', icon: '✓' },
    { id: 'achievements', label: 'Achievements', icon: '★' },
  ]},
  { section: 'STRATEGY', items: [
    { id: 'strategy', label: 'Strategy Logic', icon: '◉' },
    { id: 'indicators', label: 'Indicators', icon: '≋' },
    { id: 'sessions', label: 'Sessions & Macro', icon: '◷' },
  ]},
  { section: 'LEARNING', items: [
    { id: 'examples', label: 'Trade Examples', icon: '◈' },
    { id: 'mistakes', label: 'Fatal Mistakes', icon: '⚠' },
    { id: 'plan', label: '30-Day Plan', icon: '◫' },
  ]},
  { section: 'ACCOUNT', items: [
    { id: 'billing', label: 'Billing', icon: '◇' },
  ]},
];

// ══════════════════════════════════════════════════
// MAIN APP SHELL
// ══════════════════════════════════════════════════
export default function App() {
  const { user, profile, signOut } = useAuth();
  const [page, setPage] = useState('dashboard');
  const time = useClock();
  const sessions = getSessionStates();
  const activeSession = sessions.find(s => s.open && s.prime) || sessions.find(s => s.open);
  const utc = time.toUTCString().slice(17, 25);

  // Subscription access gate
  const isAdmin = profile?.role === 'admin';
  const subActive = profile && (
    isAdmin ||
    (['active','authenticated'].includes(profile.subscription_status) &&
     profile.subscription_current_period_end &&
     new Date(profile.subscription_current_period_end) > new Date())
  );

  // If no active subscription, restrict to billing page only
  useEffect(() => {
    if (profile && !subActive && page !== 'billing') {
      setPage('billing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Land on payment-success URL → billing
  useEffect(() => {
    if (window.location.pathname === '/billing' || window.location.search.includes('status=')) {
      setPage('billing');
    }
  }, []);

  function renderPage() {
    // Access gate — without active subscription, only Billing renders
    if (!subActive && page !== 'billing') {
      return <Billing />;
    }
    switch (page) {
      case 'dashboard': return <Dashboard onNav={setPage} />;
      case 'calculator': return <Calculator />;
      case 'journal': return <Journal />;
      case 'analytics': return <Analytics />;
      case 'montecarlo': return <MonteCarlo />;
      case 'bias': return <DailyBias />;
      case 'aicoach': return <AICoach />;
      case 'risk': return <RiskManager />;
      case 'achievements': return <Achievements />;
      case 'billing': return <Billing />;
      case 'checklist': return <Checklist />;
      case 'strategy': return <Strategy />;
      case 'indicators': return <Indicators />;
      case 'sessions': return <Sessions />;
      case 'examples': return <TradeExamples />;
      case 'mistakes': return <FatalMistakes />;
      case 'plan': return <ThirtyDayPlan />;
      case 'admin': return <AdminPanel />;
      default: return <Dashboard onNav={setPage} />;
    }
  }

  const userName = profile?.full_name || user?.email?.split('@')[0] || 'Trader';

  return (
    <div className="shell">
      {/* TOPBAR */}
      <div className="topbar">
        <div className="topbar-brand">
          <div style={{width:36,height:36,overflow:'hidden',flexShrink:0}}><Logo /></div>
          <div>
            <div className="topbar-brand-name">Trading Buddha</div>
            <div className="topbar-brand-sub">XAUUSD PRO</div>
          </div>
        </div>

        <div className="topbar-center">
          <GoldTicker />
          <div className="session-pill">
            <span className={`session-dot ${activeSession?.open ? (activeSession.prime ? 'prime' : 'active') : ''}`}></span>
            {sessions[0]?.marketClosed
              ? 'Markets Closed — Weekend'
              : activeSession?.open
                ? activeSession.name + ' Session'
                : 'Markets Quiet'}
          </div>
        </div>

        <div className="topbar-right">
          <div className="utc-clock">{utc} UTC</div>
          <div className="user-pill">
            <span style={{fontSize:10}}>●</span>
            {userName}
            {profile?.role === 'admin' && <span className="badge gold" style={{fontSize:9,padding:'1px 5px'}}>ADMIN</span>}
          </div>
          <button className="signout-btn" onClick={signOut}>Sign Out</button>
        </div>
      </div>

      <div className="body">
        {/* SIDEBAR */}
        <div className="sidebar">
          {NAV.map((group, gi) => (
            <div key={gi}>
              {group.section && <div className="sidebar-section-label">{group.section}</div>}
              {group.items.map(item => {
                const locked = !subActive && item.id !== 'billing';
                return (
                  <div key={item.id}
                    className={`sidebar-item ${item.highlight ? 'highlight' : ''} ${page === item.id ? 'active' : ''} ${locked ? 'locked' : ''}`}
                    onClick={() => locked ? setPage('billing') : setPage(item.id)}
                    title={locked ? 'Subscribe to unlock' : ''}
                  >
                    <span style={{fontSize:14,opacity:item.highlight ? 1 : 0.7}}>{item.icon}</span>
                    {item.label}
                    {item.highlight && !locked && <span className="sidebar-new-pill">NEW</span>}
                    {locked && <span style={{marginLeft:'auto',fontSize:10,opacity:0.6}}>🔒</span>}
                  </div>
                );
              })}
            </div>
          ))}
          {profile?.role === 'admin' && (
            <>
              <div className="sidebar-section-label">ADMIN</div>
              <div className={`sidebar-item admin-item ${page === 'admin' ? 'active' : ''}`}
                onClick={() => setPage('admin')}>
                <span style={{fontSize:14}}>⚙</span>
                Admin Panel
              </div>
            </>
          )}
        </div>

        {/* MAIN */}
        <div className="main">
          {renderPage()}
        </div>
      </div>
    </div>
  );
}
